import { useMsal } from "@azure/msal-react";
import { useAdminStore } from "../stores/adminStore";

/**
 * Hook to access the current user's permissions from the admin store.
 *
 * Returns:
 * - permissions: Array of permission objects { id, name }
 * - roles: Array of role objects { roleId, roleName, organizationName, organizationId }
 * - hasPermission: Function to check if user has a specific permission
 * - hasAnyPermission: Function to check if user has any of the specified permissions
 * - hasAllPermissions: Function to check if user has all specified permissions
 * - isLoaded: Boolean indicating if permissions have been loaded
 *
 * @example
 * ```tsx
 * const { hasPermission, hasAnyPermission } = usePermissions();
 *
 * // Check single permission
 * if (hasPermission("Mosquito.Read")) { ... }
 *
 * // Check any of multiple permissions
 * if (hasAnyPermission(["Mosquito.Read", "Sentinel.Read"])) { ... }
 * ```
 */
export function usePermissions() {
  const { accounts } = useMsal();

  // Get the user's object ID from MSAL token claims
  const userId =
    accounts.length > 0 ? (accounts[0].idTokenClaims as any)?.oid : null;

  // Get user detail from admin store
  const userDetail = useAdminStore((s) =>
    userId ? s.userDetail[userId] : undefined
  );

  // Extract permissions and roles from user detail
  const permissions = userDetail?.permissions ?? [];
  const roles = userDetail?.roles ?? [];

  /**
   * Check if the current user has a specific permission
   */
  const hasPermission = (permissionName: string): boolean => {
    return permissions.some((p) => p.permissionName === permissionName);
  };

  /**
   * Check if the current user has ANY of the specified permissions
   */
  const hasAnyPermission = (permissionNames: string[]): boolean => {
    return permissionNames.some((name) => hasPermission(name));
  };

  /**
   * Check if the current user has ALL of the specified permissions
   */
  const hasAllPermissions = (permissionNames: string[]): boolean => {
    return permissionNames.every((name) => hasPermission(name));
  };

  /**
   * Check if the current user has a specific role
   */
  const hasRole = (roleName: string): boolean => {
    return roles.some((r) => r.roleName === roleName);
  };

  /**
   * Check if permissions have been loaded
   */
  const isLoaded = userId !== null && userDetail !== undefined;

  return {
    permissions,
    roles,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    hasRole,
    isLoaded,
    userId,
  };
}
